export const SUPPORT_EMAIL = "support@propojo.com";
const UPDATED = "June 2026";

export const LEGAL = {
  about: {
    sections: [
      {
        heading: "Why we built it",
        body: [
          "Every agency has the same story: the work is won on the call, then two days disappear into a document. Copy-pasting last quarter's deck, re-typing pricing, fixing fonts, exporting a PDF that looks nothing like the brand.",
          "Propojo removes that gap. Describe the engagement — or paste the transcript of the call you just had — and you get a structured proposal with scope, deliverables, pricing, terms and a signature block, already wearing your brand.",
        ],
      },
      {
        heading: "How it works",
        body: [
          "Answer a short guided brief or drop in a call transcript. Propojo drafts every section, flags anything it could not verify so you never send an invented claim, and lets you rewrite, shorten, expand or translate any block in one click.",
          "Share a link or export a PDF or Word file. Clients read it on any device and accept it online — their name, role and timestamp are recorded on the document itself.",
        ],
      },
      {
        heading: "Who it is for",
        body: [
          "Independent consultants who want to look like a studio. Agencies who send proposals every week and want them consistent. Sales teams who need pricing and terms to stop drifting between documents.",
        ],
      },
      {
        heading: "Our commitments",
        body: [
          "Your content is yours. We do not sell data, and we do not use your proposals to train public models.",
          "Pricing stays transparent — the plan you see is the plan you pay, with credits that never expire on lifetime entitlements.",
          "If something breaks, a human answers. Write to " + SUPPORT_EMAIL + ".",
        ],
      },
    ],
  },

  privacy: {
    eyebrow: "Legal",
    title: "Privacy policy",
    lead: "What we collect, why we collect it, and the choices you have.",
    updated: UPDATED,
    sections: [
      {
        heading: "1. Information you give us",
        body: [
          "Account data: your name, work email, workspace name and password (stored only as a bcrypt hash — never in readable form). If you sign in with Google we receive your name, email address and profile picture from Google.",
          "Content data: the briefs, transcripts, proposals, client details, pricing and brand assets you create or upload in order to use the product.",
          "Billing data: plan, invoices and payment status. Card and UPI details are handled entirely by our payment providers — they never reach our servers.",
        ],
      },
      {
        heading: "2. Information we collect automatically",
        body: [
          "Basic usage and diagnostic data: pages visited, feature usage, approximate country, device and browser type, and timestamps. IP addresses are stored hashed where retained at all.",
          "Aggregated performance counters so we can see error rates and latency. We do not build advertising profiles and we do not sell data to anyone.",
        ],
      },
      {
        heading: "3. How we use it",
        body: [
          "To operate the product: generate proposals, render exports, share links, record client acceptance, and keep your workspace secure.",
          "To bill you correctly, prevent abuse, provide support, and send essential service email such as email verification, password resets and payment receipts.",
        ],
      },
      {
        heading: "4. AI processing",
        body: [
          "When you generate or edit a proposal, the relevant text is sent to our AI providers to produce the draft. We send only what is needed for that request, keep no third-party copies beyond the provider's own transient processing, and record only metadata — provider, model, latency, and the size of the request — never the prompt content.",
          "Your proposals are not used to train public models.",
        ],
      },
      {
        heading: "5. Service providers",
        body: [
          "We rely on a small set of processors: cloud hosting and database, AI model providers, an email delivery service for transactional email, and payment gateways. Each receives only the data required for its function.",
        ],
      },
      {
        heading: "6. Retention",
        body: [
          "Workspace and proposal data is kept while your account is active. Delete a proposal and it is removed from your workspace; request account deletion and we remove your personal data, keeping only what tax and accounting law requires us to retain for invoices.",
          "Diagnostic events are short-lived and aggregated metrics are pruned on a rolling window.",
        ],
      },
      {
        heading: "7. Security",
        body: [
          "Traffic is encrypted in transit with TLS. Passwords are hashed with bcrypt, sessions use httpOnly cookies, staff access is role-restricted and audited, and sensitive keys live only in server-side secrets.",
          "No system is perfect — if we ever discover a breach affecting your data, we will tell you.",
        ],
      },
      {
        heading: "8. Your rights",
        body: [
          "You can access, correct, export or delete your data, and object to non-essential processing. Email " + SUPPORT_EMAIL + " and we will action verified requests within 30 days.",
        ],
      },
      {
        heading: "9. Children",
        body: ["Propojo is a business tool and is not intended for anyone under 18."],
      },
      {
        heading: "10. Changes",
        body: [
          "If we make a material change we will update this page and notify account owners by email before it takes effect.",
        ],
      },
    ],
  },

  terms: {
    eyebrow: "Legal",
    title: "Terms of service",
    lead: "The agreement between you and Propojo when you use the product.",
    updated: UPDATED,
    sections: [
      {
        heading: "1. Agreement",
        body: [
          "By creating an account or using Propojo you agree to these terms. If you are accepting on behalf of a company, you confirm you are authorised to bind it.",
        ],
      },
      {
        heading: "2. Your account",
        body: [
          "You must give accurate details, confirm your email address, and keep your credentials safe. You are responsible for everything that happens under your account and for the people you invite into your workspace.",
        ],
      },
      {
        heading: "3. Plans, credits and billing",
        body: [
          "Paid plans and credit packs are charged in advance through our payment providers. Subscriptions renew for the period you selected unless cancelled before renewal; credits are consumed as you generate proposals.",
          "Prices are shown inclusive or exclusive of tax as displayed at checkout. We may change pricing for future billing periods with notice — never retroactively for a period already paid.",
        ],
      },
      {
        heading: "4. Acceptable use",
        body: [
          "Do not use Propojo to create unlawful, deceptive, defamatory or infringing material; to impersonate someone; to send spam; to resell the product as your own; or to attack, probe or overload the service.",
          "You are responsible for reviewing AI-generated drafts before sending them. Claims flagged as unverified must be checked by you.",
        ],
      },
      {
        heading: "5. Your content",
        body: [
          "You own your briefs, proposals, client data and brand assets. You grant us the limited licence needed to store, process, render and display that content so the product can function.",
          "You confirm you have the right to upload anything you put into Propojo, including logos and client information.",
        ],
      },
      {
        heading: "6. Our intellectual property",
        body: [
          "Propojo — the software, design, templates and brand — remains ours. These terms grant you a right to use the service, not a licence to copy or resell it.",
        ],
      },
      {
        heading: "7. Availability and AI output",
        body: [
          "We work hard to keep Propojo available but do not promise uninterrupted service; maintenance and provider outages happen. AI output is generated text and may contain errors — it is a draft, not professional or legal advice.",
        ],
      },
      {
        heading: "8. Suspension and termination",
        body: [
          "You can stop using Propojo at any time. We may suspend or close an account that breaches these terms, is fraudulent, or is not paid for. Where reasonable we will warn you first and give you an opportunity to export your data.",
        ],
      },
      {
        heading: "9. Liability",
        body: [
          "To the extent permitted by law, Propojo is provided as-is and our total liability for any claim is limited to the amount you paid us in the twelve months before the claim. We are not liable for indirect or consequential loss, including lost profit or lost business.",
        ],
      },
      {
        heading: "10. Governing law and contact",
        body: [
          "These terms are governed by the laws of India, and disputes are subject to the courts there. Questions: " + SUPPORT_EMAIL + ".",
        ],
      },
    ],
  },

  refund: {
    eyebrow: "Legal",
    title: "Refund policy",
    lead: "Straightforward rules, written so you do not need a lawyer to read them.",
    updated: UPDATED,
    sections: [
      {
        heading: "1. Free trial first",
        body: [
          "Every workspace can generate proposals on the free trial before paying, so you can judge the output quality with your own briefs. We recommend using it — it is the fairest way to know whether Propojo suits you.",
        ],
      },
      {
        heading: "2. 7-day refund window on first purchase",
        body: [
          "If your first paid subscription payment was made within the last 7 days and you have generated no more than 3 proposals on that plan, email " + SUPPORT_EMAIL + " and we will refund it in full.",
          "Include the email address on the account and the payment reference so we can find it quickly.",
        ],
      },
      {
        heading: "3. Renewals",
        body: [
          "Renewal payments are not automatically refundable, because the period is reserved for you. Cancel before the renewal date to avoid the next charge — you keep access until the current period ends.",
          "If a renewal charged you by mistake, or you were charged twice, tell us and we will fix it.",
        ],
      },
      {
        heading: "4. Credit packs",
        body: [
          "Unused credit packs can be refunded within 7 days of purchase. Credits already spent on generated proposals cannot be refunded, since the AI cost has been incurred.",
        ],
      },
      {
        heading: "5. Failed, duplicate or pending payments",
        body: [
          "If a payment is deducted but your plan or credits did not activate, contact us with the payment reference. We will either activate what you paid for or refund it in full — your choice.",
          "Payments that fail or stay pending at the gateway are never charged as active plans.",
        ],
      },
      {
        heading: "6. Service failure",
        body: [
          "If Propojo is unusable for an extended period because of a fault on our side, we will credit or refund the affected part of your billing period, even outside the 7-day window.",
        ],
      },
      {
        heading: "7. How refunds are processed",
        body: [
          "Approved refunds go back to the original payment method through the same gateway used for the purchase. Banks typically take 5–10 working days to show it.",
          "We do not charge a processing fee on refunds.",
        ],
      },
      {
        heading: "8. How to request one",
        body: [
          "Email " + SUPPORT_EMAIL + " with the subject 'Refund request', your account email and the payment reference. We reply within one business day and process approved refunds immediately.",
        ],
      },
    ],
  },
};
